<template>
  <transition name="slide-up">
    <div
      v-if="modelValue"
      class="marvin-chat fixed z-[99999] bottom-4 w-[95%] md:bottom-8 md:w-[420px] left-1/2 translate-x-[-50%] md:translate-x-0 md:right-8 md:left-auto shadow-xl rounded-2xl overflow-hidden"
    >
      <!-- Header -->
      <div class="marvin-header">
        <div class="flex items-center gap-3">
          <div class="header-guy">
            <div class="guy-body-sm"><div class="guy-eye-sm l"></div><div class="guy-eye-sm r"></div></div>
          </div>
          <div>
            <h3 class="text-white font-semibold text-sm">Marvin</h3>
            <p class="text-white/60 text-xs">AI {{ $t("marvin.assistant") }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="chatHistory.length > 0"
            class="reset-btn"
            @click="resetChat"
          >
            <i class="fa-solid fa-rotate-left"></i>
          </button>
          <button class="close-btn" @click="$emit('update:modelValue', false)">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Chat Body -->
      <div class="marvin-body" ref="chatBodyRef">
        <transition name="fade" mode="out-in">
        <!-- Greeting -->
        <div v-if="chatHistory.length === 0" key="greeting" class="greeting-container">
          <div class="greeting-guy">
            <div class="guy-body-lg"><div class="guy-eye-lg l"></div><div class="guy-eye-lg r"></div></div>
            <div class="guy-shadow-lg"></div>
          </div>
          <h2 class="greeting-title">{{ $t("marvin.greeting") }}</h2>
          <p class="greeting-subtitle">{{ $t("marvin.subtitle") }}</p>
          <div class="quick-actions">
            <button
              v-for="action in quickActions"
              :key="action.key"
              class="quick-action-btn"
              @click="sendQuickAction(action)"
            >
              <i :class="['fa-solid', action.icon, 'quick-action-icon']"></i>
              {{ action.label }}
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div v-else key="messages">
          <div v-for="(msg, idx) in chatHistory" :key="idx" class="message-row">
            <div v-if="msg.role === 'user'" class="flex justify-end">
              <div class="user-bubble">{{ msg.text }}</div>
            </div>
            <div v-else class="flex justify-start gap-2 items-end">
              <div class="msg-guy">
                <div class="guy-body-xs"><div class="guy-eye-xs l"></div><div class="guy-eye-xs r"></div></div>
              </div>
              <div class="ai-bubble" v-html="formatMessage(msg.text)"></div>
            </div>
          </div>
          <div v-if="isStreaming" class="flex justify-start gap-2 items-end">
            <div class="msg-guy typing">
              <div class="guy-body-xs"><div class="guy-eye-xs l"></div><div class="guy-eye-xs r"></div></div>
            </div>
            <div class="ai-bubble">
              <span v-if="streamingText" v-html="formatMessage(streamingText)"></span>
              <span v-else class="typing-dots"><span></span><span></span><span></span></span>
            </div>
          </div>
        </div>
        </transition>
      </div>

      <!-- Input -->
      <div class="marvin-input-wrapper">
        <form class="marvin-input-form" @submit.prevent="onSend">
          <input
            v-model="input"
            :disabled="isStreaming"
            type="text"
            :placeholder="$t('marvin.placeholder')"
            class="marvin-input"
          />
          <button class="send-btn" :disabled="isStreaming || !input.trim()" type="submit">
            <i v-if="isStreaming" class="fa-solid fa-circle-notch fa-spin"></i>
            <i v-else class="fa-solid fa-arrow-up"></i>
          </button>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { sendMarvinMessage, streamMarvinResponse } from "@/services/marvin.service";

const { t, locale } = useI18n();

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(["update:modelValue"]);

const input = ref("");
const isStreaming = ref(false);
const streamingText = ref("");
const chatHistory = ref([]);
const chatBodyRef = ref(null);

const allQuickActions = [
  { key: "day", icon: "fa-sun", ru: "Как дела сегодня?", uz: "Bugun qanday kunlar?" },
  { key: "student", icon: "fa-child", ru: "Сегодня был интересный ученик", uz: "Bugun qiziq o'quvchi bor edi" },
  { key: "dream", icon: "fa-star", ru: "Хочу чему-то научиться", uz: "Biror narsani o'rganmoqchiman" },
  { key: "mood", icon: "fa-face-smile", ru: "Расскажи что-нибудь весёлое", uz: "Qiziq gap aytib ber" },
  { key: "work", icon: "fa-briefcase", ru: "Что нравится в работе в Mars?", uz: "Mars'da ishlash nimasi yoqadi?" },
  { key: "idea", icon: "fa-lightbulb", ru: "У меня есть идея", uz: "Menda bir fikr bor" },
  { key: "tired", icon: "fa-mug-hot", ru: "Устал сегодня...", uz: "Bugun charchadim..." },
  { key: "funny", icon: "fa-face-laugh", ru: "Смешной случай на работе", uz: "Ishda kulgili voqea bo'ldi" },
  { key: "wish", icon: "fa-wand-magic-sparkles", ru: "Если бы я мог изменить одну вещь в Mars...", uz: "Mars'da bitta narsani o'zgartira olsam..." },
  { key: "hello", icon: "fa-hand", ru: "Привет, Марвин!", uz: "Salom, Marvin!" },
];

function pickRandom(arr, n) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

const quickActions = computed(() =>
  pickRandom(allQuickActions, 3).map((a) => ({
    key: a.key,
    icon: a.icon,
    label: locale.value === "uz" ? a.uz : a.ru,
  }))
);

function resetChat() {
  chatHistory.value = [];
  streamingText.value = "";
  isStreaming.value = false;
  input.value = "";
}

function formatMessage(text) {
  if (!text) return "";
  return text
    .replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre class="code-block"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br>");
}

function scrollToBottom() {
  nextTick(() => {
    if (chatBodyRef.value) chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
  });
}

async function onSend() {
  if (!input.value.trim() || isStreaming.value) return;
  const message = input.value.trim();

  chatHistory.value.push({ role: "user", text: message });
  input.value = "";
  isStreaming.value = true;
  streamingText.value = "";
  scrollToBottom();

  try {
    const response = await sendMarvinMessage({ message });
    let fullText = "";
    for await (const chunk of streamMarvinResponse(response)) {
      fullText += chunk;
      streamingText.value = fullText;
      scrollToBottom();
    }
    chatHistory.value.push({ role: "ai", text: fullText || "..." });
  } catch (e) {
    chatHistory.value.push({ role: "ai", text: t("marvin.error") });
  }

  isStreaming.value = false;
  streamingText.value = "";
  scrollToBottom();
}

function sendQuickAction(action) {
  input.value = action.label;
  onSend();
}

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      isStreaming.value = false;
      streamingText.value = "";
    }
  }
);

onBeforeUnmount(() => {
  isStreaming.value = false;
});
</script>

<style lang="scss">
.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(20px); opacity: 0; }

/* ── Guy character shared ── */
@keyframes guyBlink {
  0%, 42%, 44%, 100% { transform: scaleY(1); }
  43% { transform: scaleY(0.1); }
}
@keyframes guyBob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

/* ── Header guy (32px) ── */
.header-guy { flex-shrink: 0; }
.guy-body-sm {
  width: 32px; height: 35px; position: relative;
  background: radial-gradient(ellipse at 42% 38%, #eef0f6, #dce0ea 55%, #c5c9d6);
  border-radius: 50% 50% 46% 54% / 58% 55% 45% 42%;
}
.guy-eye-sm {
  width: 3.5px; height: 4.5px; background: #2a2d3a; border-radius: 50%;
  position: absolute; top: 38%; animation: guyBlink 4s ease infinite;
  &.l { left: 28%; }
  &.r { right: 28%; animation-delay: -1.5s; }
}

/* ── Greeting guy (64px) ── */
.greeting-guy { margin-bottom: 16px; animation: guyBob 2.5s ease-in-out infinite; text-align: center; }
.guy-body-lg {
  width: 64px; height: 70px; position: relative; margin: 0 auto;
  background: radial-gradient(ellipse at 42% 38%, #eef0f6, #dce0ea 55%, #c5c9d6);
  border-radius: 50% 50% 46% 54% / 58% 55% 45% 42%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}
.guy-eye-lg {
  width: 6px; height: 7px; background: #2a2d3a; border-radius: 50%;
  position: absolute; top: 38%; animation: guyBlink 4s ease infinite;
  &.l { left: 28%; }
  &.r { right: 28%; animation-delay: -1.5s; }
}
.guy-shadow-lg {
  width: 48px; height: 7px; margin: 4px auto 0;
  background: rgba(0, 0, 0, 0.08); border-radius: 50%;
}

/* ── Message guy (24px) ── */
.msg-guy { flex-shrink: 0; margin-bottom: 4px; }
.msg-guy.typing { animation: guyBob 1s ease-in-out infinite; }
.guy-body-xs {
  width: 24px; height: 26px; position: relative;
  background: radial-gradient(ellipse at 42% 38%, #eef0f6, #dce0ea 55%, #c5c9d6);
  border-radius: 50% 50% 46% 54% / 58% 55% 45% 42%;
}
.guy-eye-xs {
  width: 3px; height: 3.5px; background: #2a2d3a; border-radius: 50%;
  position: absolute; top: 38%; animation: guyBlink 4s ease infinite;
  &.l { left: 26%; }
  &.r { right: 26%; animation-delay: -2s; }
}

/* ── Layout ── */
.marvin-chat { background: #fff; display: flex; flex-direction: column; max-height: 85vh; min-height: 480px; }
.marvin-header {
  display: flex; align-items: center; justify-content: space-between; padding: 12px 16px;
  background: linear-gradient(135deg, #1e1e2e, #2a2a3e);
  .close-btn, .reset-btn {
    width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.6); display: flex; align-items: center; justify-content: center;
    border: none; cursor: pointer; font-size: 13px; transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.15); color: #fff; }
  }
}
.marvin-body { flex: 1; padding: 16px; overflow-y: auto; background: #fafbfc; &::-webkit-scrollbar { display: none; } }

/* ── Greeting ── */
.greeting-container { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 24px 16px 8px; }
.greeting-title { font-size: 20px; font-weight: 700; color: #1a1a2e; margin-bottom: 6px; }
.greeting-subtitle { font-size: 13px; color: #6b7280; max-width: 280px; line-height: 1.5; margin-bottom: 20px; }
.quick-actions { display: flex; flex-direction: column; gap: 8px; width: 100%; max-width: 300px; }
.quick-action-btn {
  display: flex; align-items: center; gap: 10px; padding: 11px 16px;
  border-radius: 12px; border: 1.5px solid #e5e7eb; background: #fff;
  color: #374151; font-size: 13px; text-align: left; cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: #f0f0f8; border-color: #c5c9d6; color: #1a1a2e; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
}
.quick-action-icon { font-size: 13px; color: #9ca3af; flex-shrink: 0; transition: color 0.15s; }
.quick-action-btn:hover .quick-action-icon { color: #6366f1; }

/* ── Messages ── */
.message-row { margin-bottom: 12px; }
@keyframes bubbleIn {
  0% { opacity: 0; transform: translateY(8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
.user-bubble {
  display: inline-block; background: #1e1e2e; color: rgba(255,255,255,0.92);
  padding: 10px 14px; border-radius: 16px 16px 4px 16px; max-width: 80%;
  font-size: 14px; line-height: 1.5; white-space: pre-line;
  animation: bubbleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.ai-bubble {
  display: block; background: #fff; color: #1f2937;
  padding: 10px 14px; border-radius: 16px 16px 16px 4px; max-width: 85%;
  font-size: 14px; line-height: 1.6; word-break: break-word;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  animation: bubbleIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.code-block { background: #1e1e2e; color: #cdd6f4; padding: 12px; border-radius: 8px; margin: 8px 0; overflow-x: auto; font-size: 13px; white-space: pre; display: block; }
.inline-code { background: #f0f0f8; color: #6366f1; padding: 2px 6px; border-radius: 4px; font-size: 13px; }

/* ── Typing dots ── */
.typing-dots { display: inline-flex; gap: 4px;
  span { width: 6px; height: 6px; background: #c5c9d6; border-radius: 50%;
    animation: typing-bounce 1.4s infinite ease-in-out;
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}
@keyframes typing-bounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }

/* ── Input ── */
.marvin-input-wrapper { padding: 12px 16px; border-top: 1px solid #f0f0f4; background: #fff; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.marvin-input-form {
  display: flex; align-items: center; gap: 8px;
  background: #f5f5f8; border: 1.5px solid #e5e7eb; border-radius: 14px; padding: 4px 4px 4px 14px;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  &:focus-within { border-color: #c5c9d6; background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,0.06); }
}
.marvin-input { flex: 1; border: none; outline: none; background: transparent; font-size: 14px; color: #1f2937; padding: 6px 0; &::placeholder { color: #9ca3af; } }
.send-btn {
  width: 34px; height: 34px; border-radius: 10px; background: #1e1e2e; color: #fff;
  display: flex; align-items: center; justify-content: center; border: none; cursor: pointer;
  font-size: 14px; flex-shrink: 0; transition: background 0.15s;
  &:hover:not(:disabled) { background: #2a2a3e; }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
}
</style>
